// Therapist — Diary / reminders.
//
// Title, date, optional time, optional separate reminder date/time,
// optional linked child, notes. Groups into Upcoming / Overdue / Completed.
// All times are South African time (SAST). Reminders arrive as in-app
// notifications (exact time, or the 07:00 morning summary for date-only).
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, Check, Loader2, Bell, Pencil, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' });
const EMPTY = () => ({ title: '', date: today(), time: '', reminder_date: '', reminder_time: '', child_id: '', notes: '' });
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const reminderLabel = (r) => {
  if (!r.reminder_date && !r.reminder_time) return null;
  return `Reminder ${r.reminder_date || r.date}${r.reminder_time ? ` ${r.reminder_time}` : ' 07:00'}`;
};

function DiaryRow({ r, highlight, onToggle, onEdit, onRemove }) {
  return (
    <div
      id={`diary-entry-${r.id}`}
      className={`flex items-center gap-2 py-1.5 border-b border-stone-100 last:border-b-0 ${highlight ? 'bg-amber-50 rounded' : ''}`}
      data-testid={`diary-row-${r.id}`}
    >
      <button
        onClick={() => onToggle(r)}
        className={`w-5 h-5 rounded border flex items-center justify-center ${r.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-stone-300'}`}
        aria-label="Toggle complete"
        data-testid={`diary-toggle-${r.id}`}
      >
        {r.completed && <Check className="w-3 h-3" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${r.completed ? 'line-through text-stone-400' : 'text-stone-900'}`}>{r.title}</div>
        <div className="text-[11px] text-stone-500">
          {r.date}{r.time && ` · ${r.time}`}
          {r.child_name && ` · ${r.child_name}`}
          {reminderLabel(r) && ` · ${reminderLabel(r)}`}
        </div>
      </div>
      <button onClick={() => onEdit(r)} className="text-stone-400 hover:text-stone-700 p-1" aria-label="Edit" data-testid={`diary-edit-${r.id}`}>
        <Pencil className="w-4 h-4" />
      </button>
      <button onClick={() => onRemove(r)} className="text-stone-400 hover:text-rose-600 p-1" aria-label="Delete" data-testid={`diary-delete-${r.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function TherapistDiary() {
  const [items, setItems] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY());
  const [editingId, setEditingId] = useState(null);
  const [params] = useSearchParams();
  const focusId = params.get('entry');

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/reminders`, { ...auth(), params: { status: 'all' } }),
        axios.get(`${API}/children`, auth()),
      ]);
      setItems(r.data || []);
      setChildren(c.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!loading && focusId) document.getElementById(`diary-entry-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [loading, focusId]);

  const save = async () => {
    if (!draft.title.trim()) { toast.error('Title required'); return; }
    const body = {
      ...draft,
      time: draft.time || null,
      reminder_date: draft.reminder_date || null,
      reminder_time: draft.reminder_time || null,
      child_id: draft.child_id || null,
    };
    try {
      if (editingId) await axios.put(`${API}/reminders/${editingId}`, body, auth());
      else await axios.post(`${API}/reminders`, body, auth());
      setDraft(EMPTY()); setEditingId(null);
      await load();
      toast.success(editingId ? 'Entry updated' : 'Reminder added');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please check the date and time');
    }
  };

  const edit = (r) => {
    setEditingId(r.id);
    setDraft({
      title: r.title || '', date: r.date, time: r.time || '', reminder_date: r.reminder_date || '',
      reminder_time: r.reminder_time || '', child_id: r.child_id || '', notes: r.notes || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const cancelEdit = () => { setEditingId(null); setDraft(EMPTY()); };
  const toggle = async (r) => { await axios.put(`${API}/reminders/${r.id}`, { completed: !r.completed }, auth()); await load(); };
  const remove = async (r) => { await axios.delete(`${API}/reminders/${r.id}`, auth()); await load(); };

  const t = today();
  const overdue = items.filter((r) => !r.completed && r.date < t);
  const upcoming = items.filter((r) => !r.completed && r.date >= t);
  const completed = items.filter((r) => r.completed);
  const row = (r) => <DiaryRow key={r.id} r={r} highlight={r.id === focusId} onToggle={toggle} onEdit={edit} onRemove={remove} />;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="therapist-diary-page">
      <div>
        <h1 className="text-2xl font-serif text-stone-900">Diary & reminders</h1>
        <p className="text-sm text-stone-600 mt-1">
          Quick professional to-dos (South African time). You'll get a notification at the entry's time —
          or at a separate reminder time you choose. Entries without a time appear in the 07:00 morning summary.
          Text <span className="font-medium">diary</span> to the PlayFirst WhatsApp number to see what's coming up.
        </p>
      </div>

      <Card className="p-4 space-y-2" data-testid="diary-composer">
        {editingId && (
          <div className="flex items-center justify-between text-xs text-amber-800 bg-amber-50 rounded px-2 py-1" data-testid="diary-editing-banner">
            Editing entry
            <button onClick={cancelEdit} className="p-0.5" aria-label="Cancel edit" data-testid="diary-cancel-edit-btn"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="e.g. Call Sam's parent about AAC trial"
          onKeyDown={(e) => e.key === 'Enter' && save()}
          data-testid="diary-title-input"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="diary-date-input" />
          <Input type="time" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} placeholder="Time (optional)" data-testid="diary-time-input" />
          <select
            className="border rounded-md px-2 py-1.5 text-sm"
            value={draft.child_id}
            onChange={(e) => setDraft({ ...draft, child_id: e.target.value })}
            data-testid="diary-child-select"
          >
            <option value="">Link to child (optional)</option>
            {children.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button onClick={save} data-testid="diary-add-btn">
            {editingId ? <><Check className="w-4 h-4 mr-1" /> Save</> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
          <Bell className="w-3.5 h-3.5" /> Remind me (optional, defaults to the entry time):
          <Input type="date" value={draft.reminder_date} onChange={(e) => setDraft({ ...draft, reminder_date: e.target.value })} className="h-8 w-40" data-testid="diary-reminder-date-input" />
          <Input type="time" value={draft.reminder_time} onChange={(e) => setDraft({ ...draft, reminder_time: e.target.value })} className="h-8 w-28" data-testid="diary-reminder-time-input" />
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center p-10">
          <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C]" />
        </div>
      ) : (
        <>
          {overdue.length > 0 && (
            <Card className="p-4" data-testid="diary-overdue-section">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-rose-600" />
                <h2 className="font-medium text-stone-900">Overdue ({overdue.length})</h2>
              </div>
              {overdue.map(row)}
            </Card>
          )}
          <Card className="p-4" data-testid="diary-upcoming-section">
            <h2 className="font-medium text-stone-900 mb-2">Upcoming ({upcoming.length})</h2>
            {upcoming.length === 0 && <p className="text-sm text-stone-500">Nothing scheduled.</p>}
            {upcoming.map(row)}
          </Card>
          {completed.length > 0 && (
            <Card className="p-4" data-testid="diary-completed-section">
              <h2 className="font-medium text-stone-500 mb-2">Completed ({completed.length})</h2>
              {completed.slice(0, 10).map(row)}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
